import { z } from 'zod'
import {
  ENTITY_TYPES,
  type CreditApplicationApContact,
  type CreditApplicationAttestation,
  type CreditApplicationBank,
  type CreditApplicationCompany,
  type CreditApplicationDisclosures,
  type CreditReferenceInput,
  type EntityType,
  type SubmitCreditApplicationPayload,
} from '@/lib/api-types'
import { zodFieldErrors, type FieldErrors } from '@/lib/zod-errors'

/**
 * Client-side mirror of the API's credit-application zod schema
 * (greenline-hub-api routes/client/credit-applications.ts). The wizard
 * validates each step against these before advancing, so a form that clears
 * the steps produces a payload the server accepts as-is.
 *
 * Two step checks are deliberately STRICTER than the API (which keeps the
 * fields optional): `entityTypeOther` is required when the entity type is
 * Other, and a checked disclosure requires its details — the paper form
 * expects both, and a blank would leave the credit team guessing.
 */

const dateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)

export const companySchema = z.object({
  companyName: z.string().trim().min(1).max(255),
  companyAddress: z.string().trim().min(1).max(500),
  dba: z.string().trim().max(255).optional(),
  phone: z.string().trim().min(1).max(40),
  federalTaxId: z.string().trim().min(1).max(40),
  duns: z.string().trim().max(40).optional(),
  typeOfBusiness: z.string().trim().max(255).optional(),
  employeeCount: z.number().int().nonnegative().max(1_000_000).optional(),
  dateEstablished: dateString.optional(),
  productsToPurchase: z.string().trim().max(1000).optional(),
  entityType: z.enum(ENTITY_TYPES),
  entityTypeOther: z.string().trim().max(120).optional(),
  stateOrganized: z.string().trim().max(80).optional(),
  authorizedPersons: z.string().trim().max(1000).optional(),
})

export const apContactSchema = z.object({
  name: z.string().trim().min(1).max(120),
  phone: z.string().trim().max(40).optional(),
  email: z.email().max(200),
  invoiceEmail: z.email().max(200).optional(),
})

export const bankSchema = z.object({
  bankName: z.string().trim().min(1).max(255),
  accountNumber: z.string().trim().min(1).max(60),
  contactName: z.string().trim().max(120).optional(),
  address: z.string().trim().max(500).optional(),
  phone: z.string().trim().max(40).optional(),
})

export const disclosuresSchema = z.object({
  bankruptcy: z.boolean(),
  bankruptcyDetails: z.string().trim().max(2000).optional(),
  litigation: z.boolean(),
  litigationDetails: z.string().trim().max(2000).optional(),
})

export const attestationSchema = z.object({
  signedName: z.string().trim().min(1).max(120),
  signedTitle: z.string().trim().min(1).max(120),
  signedDate: dateString,
  termsAccepted: z.literal(true),
})

export const referenceSchema = z.object({
  name: z.string().trim().min(1).max(255),
  address: z.string().trim().max(500).optional(),
  phone: z.string().trim().max(40).optional(),
  fax: z.string().trim().max(40).optional(),
  email: z.email().max(200),
})

/** The full POST body — field-for-field the API's applicationSchema. */
export const applicationSchema = z.object({
  company: companySchema,
  apContact: apContactSchema,
  bank: bankSchema,
  disclosures: disclosuresSchema,
  attestation: attestationSchema,
  references: z.array(referenceSchema).min(2).max(4),
})

// Step schemas: the mirrors plus the two paper-form refinements.
const companyStepSchema = companySchema.superRefine((val, ctx) => {
  if (val.entityType === 'Other' && !val.entityTypeOther) {
    ctx.addIssue({ code: 'custom', path: ['entityTypeOther'], message: 'Describe the entity type.' })
  }
})

const disclosuresStepSchema = disclosuresSchema.superRefine((val, ctx) => {
  if (val.bankruptcy && !val.bankruptcyDetails) {
    ctx.addIssue({
      code: 'custom',
      path: ['bankruptcyDetails'],
      message: 'Add a short note on the filing.',
    })
  }
  if (val.litigation && !val.litigationDetails) {
    ctx.addIssue({
      code: 'custom',
      path: ['litigationDetails'],
      message: 'Add a short note on the litigation.',
    })
  }
})

// ---------------------------------------------------------------------------
// Form state (string-typed inputs) → API payload
// ---------------------------------------------------------------------------

export const MIN_REFERENCES = 2
export const MAX_REFERENCES = 4

export interface ReferenceFormRow {
  key: string
  name: string
  address: string
  phone: string
  fax: string
  email: string
}

export interface ApplicationFormState {
  company: {
    companyName: string
    companyAddress: string
    dba: string
    phone: string
    federalTaxId: string
    duns: string
    typeOfBusiness: string
    employeeCount: string
    dateEstablished: string
    productsToPurchase: string
    entityType: '' | EntityType
    entityTypeOther: string
    stateOrganized: string
    authorizedPersons: string
  }
  apContact: { name: string; phone: string; email: string; invoiceEmail: string }
  bank: { bankName: string; accountNumber: string; contactName: string; address: string; phone: string }
  disclosures: {
    bankruptcy: boolean
    bankruptcyDetails: string
    litigation: boolean
    litigationDetails: string
  }
  attestation: { signedName: string; signedTitle: string; signedDate: string; termsAccepted: boolean }
  references: ReferenceFormRow[]
}

export function emptyReferenceRow(key: string): ReferenceFormRow {
  return { key, name: '', address: '', phone: '', fax: '', email: '' }
}

export function emptyApplicationForm(): ApplicationFormState {
  return {
    company: {
      companyName: '',
      companyAddress: '',
      dba: '',
      phone: '',
      federalTaxId: '',
      duns: '',
      typeOfBusiness: '',
      employeeCount: '',
      dateEstablished: '',
      productsToPurchase: '',
      entityType: '',
      entityTypeOther: '',
      stateOrganized: '',
      authorizedPersons: '',
    },
    apContact: { name: '', phone: '', email: '', invoiceEmail: '' },
    bank: { bankName: '', accountNumber: '', contactName: '', address: '', phone: '' },
    disclosures: { bankruptcy: false, bankruptcyDetails: '', litigation: false, litigationDetails: '' },
    attestation: {
      signedName: '',
      signedTitle: '',
      signedDate: new Date().toISOString().slice(0, 10),
      termsAccepted: false,
    },
    references: [emptyReferenceRow('ref-1'), emptyReferenceRow('ref-2')],
  }
}

/** '' → undefined so optional fields are OMITTED from the payload, not sent blank. */
function opt(value: string): string | undefined {
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

function buildCompany(form: ApplicationFormState): CreditApplicationCompany {
  const c = form.company
  const employeeCount = c.employeeCount.trim()
  return {
    companyName: c.companyName.trim(),
    companyAddress: c.companyAddress.trim(),
    dba: opt(c.dba),
    phone: c.phone.trim(),
    federalTaxId: c.federalTaxId.trim(),
    duns: opt(c.duns),
    typeOfBusiness: opt(c.typeOfBusiness),
    employeeCount: employeeCount === '' ? undefined : Number.parseInt(employeeCount, 10),
    dateEstablished: opt(c.dateEstablished),
    productsToPurchase: opt(c.productsToPurchase),
    entityType: c.entityType as EntityType,
    entityTypeOther: c.entityType === 'Other' ? opt(c.entityTypeOther) : undefined,
    stateOrganized: opt(c.stateOrganized),
    authorizedPersons: opt(c.authorizedPersons),
  }
}

function buildApContact(form: ApplicationFormState): CreditApplicationApContact {
  const a = form.apContact
  return { name: a.name.trim(), phone: opt(a.phone), email: a.email.trim(), invoiceEmail: opt(a.invoiceEmail) }
}

function buildBank(form: ApplicationFormState): CreditApplicationBank {
  const b = form.bank
  return {
    bankName: b.bankName.trim(),
    accountNumber: b.accountNumber.trim(),
    contactName: opt(b.contactName),
    address: opt(b.address),
    phone: opt(b.phone),
  }
}

function buildDisclosures(form: ApplicationFormState): CreditApplicationDisclosures {
  const d = form.disclosures
  return {
    bankruptcy: d.bankruptcy,
    bankruptcyDetails: d.bankruptcy ? opt(d.bankruptcyDetails) : undefined,
    litigation: d.litigation,
    litigationDetails: d.litigation ? opt(d.litigationDetails) : undefined,
  }
}

function buildAttestation(form: ApplicationFormState): CreditApplicationAttestation {
  const a = form.attestation
  return {
    signedName: a.signedName.trim(),
    signedTitle: a.signedTitle.trim(),
    signedDate: a.signedDate,
    termsAccepted: a.termsAccepted as true,
  }
}

function buildReferences(form: ApplicationFormState): CreditReferenceInput[] {
  return form.references.map((row) => ({
    name: row.name.trim(),
    address: opt(row.address),
    phone: opt(row.phone),
    fax: opt(row.fax),
    email: row.email.trim(),
  }))
}

/** The exact POST body — undefined optionals drop out of the JSON entirely. */
export function buildApplicationPayload(form: ApplicationFormState): SubmitCreditApplicationPayload {
  return {
    company: buildCompany(form),
    apContact: buildApContact(form),
    bank: buildBank(form),
    disclosures: buildDisclosures(form),
    attestation: buildAttestation(form),
    references: buildReferences(form),
  }
}

/**
 * Per-step validation, keyed to match the wizard's field ids
 * (`company.companyName`, `references.0.email`, …). Empty result = advance.
 */
export function validateStep(step: number, form: ApplicationFormState): FieldErrors {
  switch (step) {
    case 0:
      return zodFieldErrors(companyStepSchema.safeParse(buildCompany(form)), 'company')
    case 1:
      return {
        ...zodFieldErrors(apContactSchema.safeParse(buildApContact(form)), 'apContact'),
        ...zodFieldErrors(bankSchema.safeParse(buildBank(form)), 'bank'),
      }
    case 2:
      return zodFieldErrors(
        z.array(referenceSchema).min(MIN_REFERENCES).max(MAX_REFERENCES).safeParse(buildReferences(form)),
        'references',
      )
    case 3:
      return zodFieldErrors(disclosuresStepSchema.safeParse(buildDisclosures(form)), 'disclosures')
    case 4:
      return zodFieldErrors(attestationSchema.safeParse(buildAttestation(form)), 'attestation')
    default:
      return {}
  }
}
